import React, { useState } from "react";
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import styled, { withTheme } from 'styled-components'
import { Slider } from "antd";
import { Flex, FlexColumn, CardContainer } from '../../Reusable/Container'
import { StyledExtLink, StyledLink } from '../../Reusable/Link'
import { Text, Tag } from '../../Reusable/Text'
import { Image } from '../../Reusable/Image'
import { LabeledInput } from '../../Reusable/Input'
import { toCurrency } from '../../../Utils/util'
import Diamond from '../../../Images/hya-diamond.png'
import Plus from '../../../Images/hya-plus.svg'
import Minus from '../../../Images/hya-minus.svg'
import Percentage from '../../../Images/hya-percentage.svg'
import Dollar from '../../../Images/hya-dollar.svg'
import moment from 'moment'


const StyledSlider = styled(Slider)`
  .ant-slider-rail {
    height: 8px;
    background: #e8e8e8;
    border-radius: 4px;
  }

  .ant-slider-track {
    height: 8px;
    background: ${props => props.theme.colors.primary};
    border-radius: 4px;
  }

  .ant-slider-handle {
    width: 16px;
    height: 16px;
    background: linear-gradient(111deg, ${props => props.theme.colors.primary3} -29.15%, #19F 78.47%, #19F 100.52%);
    border: none;
    margin-top: -4px;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
  }

  .ant-slider-handle:hover {
    background: #40a9ff;
  }

  .ant-slider-handle:focus {
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.3);
  }

  .ant-slider-handle.ant-slider-handle-active {
    background: #096dd9;
  }
`;

const HighYieldCalculator = ({ hyaData, theme, learnMore }) => {
    const [amount, setAmount] = useState(5000000); // Default to $100,000
    const maxAmount = 10000000; // 10 million
    const minAmount = 0; // Minimum of 0

    const { hyaAPY, fdicLink, fdicAPY, fdicEffectiveDate } = hyaData

    const calculateYield = (principal, rate) => {
        return ((principal * rate) / 100).toFixed(2); // Simple interest calculation
    };

    const handleOnChange = (event) => {
        if (event) {
            const target = event.target;
            const inputValue = target.value.replace(/[^\d]/g, ""); // Remove non-numeric characters
            const numericValue = parseInt(inputValue, 10); // Parse as integer

            if (!isNaN(numericValue)) {
                setAmount(Math.min(numericValue, maxAmount)); // Cap at maxAmount
            } else {
                setAmount(0); // Default to 0 for invalid input
            }
        } else {
            setAmount(0); // Default to 0 when input is cleared
        }
    };

    return (
        <Flex start fullHeight centerH>
            {
                learnMore &&
                <FlexColumn start gap="24px" style={{ width: 570, height: "100%", padding: "48px 48px", background: "#F3FAFF", borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>
                    <Text size="20px" weight="600">Learn more</Text>

                    <Flex start gap='24px'>
                        <Image src={Plus} />
                        <FlexColumn gap="4px">
                            <Text size="16px" weight="600">Adding funds</Text>
                            <Text>Instantly transfer from your Aion account or send it from another external bank account. Your account is ready to receive ACH, Wire, RTP, and FedNow transfers.</Text>
                        </FlexColumn>
                    </Flex>

                    <Flex start gap='24px'>
                        <Image src={Minus} />
                        <FlexColumn gap="4px">
                            <Text size="16px" weight="600">Withdrawing funds</Text>
                            <Text>Withdraw funds 24/7 instantly to your other Aion account. External transfers are not supported on this account.</Text>
                        </FlexColumn>
                    </Flex>

                    <Flex start gap='24px'>
                        <Image src={Percentage} />
                        <FlexColumn gap="4px">
                            <Text size="16px" weight="600">Annual Percentage Yield (APY)</Text>
                            <Text>APY represents the total annual return taking into account the effect of monthly compounding on the interest rate.</Text>
                        </FlexColumn>
                    </Flex>

                    <Flex start gap='24px'>
                        <Image src={Dollar} />
                        <FlexColumn gap="4px">
                            <Text size="16px" weight="600">Interest payout</Text>
                            <Text>Interest is calculated as simple interest daily based on the day’s ending available balance. Monthly interest amount is paid on or before 10th calendar day of the following month.</Text>
                        </FlexColumn>
                    </Flex>
                </FlexColumn>
            }
            <FlexColumn center fullHeight fullWidth className="background-high-yield-calc">
                <FlexColumn center fullHeight fullWidth style={{ padding: 24 }}>
                    <Text size="20px" weight="700" height="24px">Estimate how much you can earn on this account</Text>
                    <Text width="426px" center height="24px">Use the slider to calculate your potential annual returns.</Text>

                    <FlexColumn fullWidth start style={{ margin: "20px 0", paddingTop: 36, borderBottom: '1px solid var(--Global-Defaults-Divider-line, #F7F7F9)' }}>
                        {/* <Flex fullWidth center>
                            <LabeledInput
                                autoFocus
                                alignCenter
                                margin='24px 0 24px'
                                width='480px'
                                id="amount"
                                textSize='20px'
                                minHeight='48px'
                                value={amount && `$ ${toCurrency(amount, 0, 0)}`}
                                placeholder="Enter the amount of funding you need"
                                onChange={handleOnChange}
                                // error={errorField === "capitalRequest"}
                                // errorMessage={errorMessage}
                                alignError={true}
                            />
                        </Flex> */}

                        <StyledSlider
                            tooltipVisible={true}
                            min={minAmount}
                            max={maxAmount}
                            step={1000} // Adjust step for finer control
                            value={amount}
                            onChange={(value) => setAmount(value)}
                            tipFormatter={(value) => `$${toCurrency(value)}`}
                        />

                        <Flex fullWidth between style={{ padding: "12px 4px 12px" }}>
                            <Text color="#8181A0">$0</Text>
                            <Text color="#8181A0">${maxAmount / 1000000}M</Text>
                        </Flex>
                    </FlexColumn>

                    <FlexColumn fullWidth start style={{ margin: "20px 0" }} gap="24px">
                        <Text size='16px' weight="600">Potential Annual Earnings</Text>
                        <Flex start gap="0" centerHorizontally>
                            <FlexColumn gap="4px" style={{ width: 160 }}>
                                <Flex start centerHorizontally style={{ marginLeft: -5 }}>
                                    <Image src={Diamond} />
                                    <Text color={theme.colors.primary3}>Aion High Yield</Text>
                                </Flex>
                                <Flex start gap="4px">
                                    <Text bottom size="16px" weight="600">{hyaAPY}%</Text>
                                    <Text bottom size="12px" margin="2px 0 0">APY{" "}<sup style={{ color: theme.colors.primary3 }}>1</sup></Text>
                                </Flex>
                            </FlexColumn>

                            <div style={{ width: '138px', height: '12px', borderRadius: 'var(--Radius-R8, 8px)', background: 'var(--Primary-Blue-Primary-Blue---80, #41ADFF)', marginRight: 24 }} />

                            <Text size="24px" weight='600'>
                                ${toCurrency(calculateYield(amount, hyaAPY))}
                            </Text>
                        </Flex>
                        <Flex start gap="0" centerHorizontally>
                            <FlexColumn gap="4px" style={{ width: 160 }}>
                                <Text color='#8181A0'>National Average</Text>
                                <Flex start gap="4px">
                                    <Text bottom size="16px" weight="600">{fdicAPY}%</Text>
                                    <Text bottom size="12px" margin="2px 0 0">APY{" "}<sup style={{ color: theme.colors.primary3 }}>2</sup></Text>
                                </Flex>
                            </FlexColumn>

                            <div style={{ width: '36px', height: '12px', borderRadius: 'var(--Radius-R8, 8px)', background: '#E6E6EC', marginRight: 24 }} />

                            <Text size="24px" weight='600'>
                                ${toCurrency(calculateYield(amount, fdicAPY))}
                            </Text>
                        </Flex>

                        <FlexColumn gap="8px" alignItems="flex-start">
                            <Text size="14px" color="#8181A0">
                                <sup style={{ color: theme.colors.primary3 }}>1</sup> Rates subject to change. Refer to{" "}
                                <StyledExtLink target="_blank" href="https://www.aionfi.com/interest-earned-on-aion-high-yield-business-accounts" style={{ textDecoration: "underline" }}>Terms of Interest Accrual</StyledExtLink>
                                .
                            </Text>
                            <Text size="14px" color="#8181A0">
                                <sup style={{ color: theme.colors.primary3 }}>2</sup> Calculated based on the national average rate on interest checking accounts <br />{" "}
                                <StyledExtLink target="_blank" href={fdicLink} style={{ textDecoration: "underline" }}>published by FDIC as of {moment(fdicEffectiveDate).format('MM/DD/YY')}</StyledExtLink>
                                .
                            </Text>
                        </FlexColumn>
                    </FlexColumn>
                </FlexColumn>
            </FlexColumn>
        </Flex>
    );
};


function mapStateToProps(state) {
    return {
        store: state.aionAppReducer,
        bankingStore: state.bankingAppReducer
    }
}

function mapDispatchToProps(dispatch) {
    return {
        dispatch
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTheme(HighYieldCalculator)))
